import { useMemo } from 'react';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import { TabTableContainer } from 'components/layout/AppLayout/AppLayout.styled';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import { ENUMS, PRICE_CONFIGURATIONS } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author Burak Ak
 */

const OTODeliveryPricesList = () => {
	const defaultSort = useMemo(() => [{ id: 'id', desc: true }], []);

	const columns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'shippingContractName',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				showOnMobile: true,
				width: 100
			},
			{
				Header: 'deliveryCompanySettingsId',
				accessor: 'deliveryCompanySettingsId',
				filterType: FilterType.number,
				width: 100,
				show: false
			},
			{
				Header: 'shippingContractId',
				accessor: 'shippingContractId',
				filterType: FilterType.number,
				width: 100
			},
			{
				Header: 'agreementType',
				accessor: 'agreementType',
				filterType: FilterType.enumMulti,
				width: 100,
				enumName: ENUMS.SHIPPING_PRICE_AGREEMENT_TYPE
			},
			{
				Header: 'status',
				accessor: 'shippingContractStatus',
				filterType: FilterType.enumMulti,
				width: 100,
				enumName: ENUMS.YES_NO
			},
			{
				Header: 'clientName',
				accessor: 'clientName',
				filterType: FilterType.string,
				showOnMobile: true,
				width: 100
			},
			{
				Header: 'clientPlan',
				accessor: 'clientPlan',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_V2_SMB_CLIENT_TYPES,
				showOnMobile: true,
				width: 100
			},
			{
				Header: 'serviceType',
				accessor: 'serviceType',
				filterType: FilterType.enumMulti,
				showOnMobile: true,
				width: 100,
				enumName: ENUMS.PRICE_CONFIGURATION_SERVICE_TYPE
			},
			{
				Header: 'country',
				accessor: 'country',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				width: 100,
				show: false
			},
			{
				Header: 'volumetricWeightRate',
				accessor: 'volumetricWeightRate',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'chargingStatus',
				accessor: 'chargingStatus',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_CHARGING_STATUS,
				show: false
			},
			{
				Header: 'basePriceMethodType',
				accessor: 'basePriceMethodType',
				filterType: FilterType.enumMulti,
				showOnMobile: true,
				enumName: ENUMS.PRICE_CONFIGURATION_BASE_PRICE_METHOD_TYPE,
				show: false
			},
			{
				Header: 'isSameCity',
				accessor: 'isSameCityAsString',
				filterType: FilterType.string,
				renderCell: ListComponents.STATUS,
				show: false
			},
			{
				Header: 'isBetweenCity',
				accessor: 'isBetweenCityAsString',
				filterType: FilterType.string,
				renderCell: ListComponents.STATUS,
				show: false
			},

			{
				Header: 'shipmentType',
				accessor: 'shipmentType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_SHIPMENT_TYPE,
				show: false
			},
			{
				Header: 'maxOrderValue',
				accessor: 'maxOrderValue',
				filterType: FilterType.number,
				width: 100,
				renderCell: ListComponents.CURRENCY,
				show: false
			},
			{
				Header: 'maxCodValue',
				accessor: 'maxCodValue',
				filterType: FilterType.number,
				width: 100,
				renderCell: ListComponents.CURRENCY,
				show: false
			},
			{
				Header: 'maxKm',
				accessor: 'maxKm',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'maxWeight',
				accessor: 'maxWeight',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'maxDimension',
				accessor: 'maxDimension',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'tempInsideWarehouse',
				accessor: 'tempInsideWarehouse',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'tempInsideVehicle',
				accessor: 'tempInsideVehicle',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'pickupType',
				accessor: 'pickupType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_PICKUP_TYPE,
				show: false
			},

			{
				Header: 'deliveryType',
				accessor: 'deliveryType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_DELIVERY_TYPE,
				show: false
			},
			{
				Header: 'insuranceAmount',
				accessor: 'insuranceFee',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'cutOffTime',
				accessor: 'cutOffTime',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'deliveryPromise',
				accessor: 'deliveryPromise',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DELIVERY_PROMISE,
				show: false
			},
			{
				Header: 'notice',
				accessor: 'notice',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'maxFreeWeight',
				accessor: 'maxFreeWeight',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'extraWeightPerKg',
				accessor: 'extraWeightPerKg',
				filterType: FilterType.float,
				width: 100,
				renderCell: ListComponents.CURRENCY,
				show: false
			},

			{
				Header: 'maxFreeKm',
				accessor: 'maxFreeKm',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'extraChargePerKm',
				accessor: 'extraChargePerKm',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY,
				show: false
			},
			{
				Header: 'applyCodRateAfter',
				accessor: 'applyCodRateAfter',
				filterType: FilterType.float,
				width: 100,
				renderCell: ListComponents.CURRENCY,
				show: false
			},
			{
				Header: 'codCharge',
				accessor: 'codCharge',
				filterType: FilterType.float,
				width: 100,
				renderCell: ListComponents.CURRENCY,
				show: false
			},
			{
				Header: 'codPercentage',
				accessor: 'codPercentage',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'cardOnDeliveryPercentage',
				accessor: 'cardOnDeliveryPercentage',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'returnPercentage',
				accessor: 'returnPercentage',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'discountPercentage',
				accessor: 'discountPercentage',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'firstPriceRate',
				accessor: 'firstPriceRate',
				showOnMobile: true,
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'fuelSurcharge',
				accessor: 'fuelSurcharge',
				filterType: FilterType.float,
				width: 100,
				renderCell: ListComponents.PERCENTAGE,
				show: false
			},
			{
				Header: 'createdDate',
				accessor: 'createdDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'createdBy',
				accessor: 'createdBy',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'modifiedDate',
				accessor: 'modifiedDate',
				filterType: FilterType.date,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'modifiedBy',
				accessor: 'modifiedBy',
				filterType: FilterType.string,
				width: 100
			}
		],
		[]
	);

	return (
		<TabTableContainer>
			<DynamicList
				id={DYNAMICLISTS.OTO_DELIVERY_PRICES}
				columns={columns}
				fetchUrl={PRICE_CONFIGURATIONS.GET_OTO_DELIVERY_PRICE_LIST}
				defaultSort={defaultSort}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="otoDeliveryPrices"
			/>
		</TabTableContainer>
	);
};

export default OTODeliveryPricesList;
